<template>
  <LayoutPage :loading="smartUnitLoading">
    <template #title>
      <v-btn icon @click="$router.go(-1)">
        <v-icon color="primary">fas fa-chevron-circle-left</v-icon>
      </v-btn>
      <v-toolbar-title class="headline">
        <span class="grey--text text--darken-2">Smart Units</span>
        <v-icon small left right>fas fa-chevron-right</v-icon>
        <template v-if="!renterId">
          <span class="grey--text text--darken-2">Inventory</span>
          <v-icon small left right>fas fa-chevron-right</v-icon>
        </template>
        <PossiblePMS as-icon :sync-obj="smartUnit" content-class="mr-2">
          {{ smartUnit.name }}
        </PossiblePMS>
      </v-toolbar-title>
    </template>

    <v-alert
      v-if="isAutoEnrollment && enrollmentMoveInAt && isAwaitingDevice"
      type="warning"
      colored-border
      border="left"
      icon="fas fa-exclamation-triangle"
      class="mb-4"
    >
      <v-row align="center" no-gutters>
        <v-col class="grow">
          <div class="font-weight-bold mb-1 warning--text">
            Assign Tenant Device
          </div>
          <div>
            Moved in <strong>{{ formatDate(enrollmentMoveInAt) }}</strong
            >. No device assigned yet —
            <v-chip
              v-if="enrollmentDays !== null"
              x-small
              :color="enrollmentDaysChipColor"
              :text-color="enrollmentDaysTextColor"
              outlined
              class="mx-1"
            >
              {{ daysWithoutDeviceLabel(enrollmentDays) }} </v-chip
            >. Assign a device to activate Smart Unit Monitoring.
          </div>
        </v-col>
      </v-row>
    </v-alert>

    <v-alert
      v-else-if="isAutoEnrollment && enrollmentMoveInAt && isActiveEnrollment"
      type="info"
      colored-border
      border="left"
      class="mb-4"
    >
      Tenant moved in on <strong>{{ formatDate(enrollmentMoveInAt) }}</strong
      >. Smart Unit Monitoring active.
    </v-alert>

    <v-row>
      <v-col lg="5" class="unit-summary-column">
        <CardSmartUnitInfo
          v-model="editingUnit"
          class="mb-4"
          @assign-device="gotoManageDevices"
        />

        <CardAlertResponders
          v-if="renterId"
          v-model="editingResponders"
          :renter-id="renterId"
          class="mb-4"
        />

        <CardFacilityZone
          v-if="smartZonesEnabled && smartUnit && smartUnit.id"
          :unit="smartUnit"
          :facility-id="facilityId"
          :loading="smartUnitLoading"
          @zone-updated="refetchUnit"
        />
      </v-col>

      <v-col lg="7">
        <CardAssignedDevices class="mb-4" />
        <CardIncidentHistory
          :unit-id="unitId"
          :renter-id="renterId"
          class="mb-4"
        />
        <CardClimateHistory
          :devices="smartUnit.devices"
          :loading="smartUnitLoading"
        />
      </v-col>
    </v-row>
  </LayoutPage>
</template>

<script>
import { ref, computed } from "vue";
import LayoutPage from "@layout/LayoutPage.vue";
import PossiblePMS from "@components/common/PossiblePMS.vue";

import CardSmartUnitInfo from "./CardSmartUnitInfo.vue";
import CardAlertResponders from "./CardAlertResponders.vue";
import CardAssignedDevices from "./CardAssignedDevices.vue";
import CardIncidentHistory from "./CardIncidentHistory.vue";

import useSmartUnit from "./useSmartUnit";
import CardClimateHistory from "./CardClimateHistory.vue";
import CardFacilityZone from "./CardFacilityZone.vue";
import { formatDate } from "@tod-ui/helpers/datetime";
import usePMS from "../common/usePMS";
import useFacility from "../authentication/useFacility";
import {
  ENROLLMENT_STATUS,
  awaitingDaysChipColor,
  awaitingDaysTextColor,
  daysWithoutDeviceLabel,
} from "./helper";

export default {
  name: "PageSmartUnitDrilldown",
  components: {
    LayoutPage,
    CardSmartUnitInfo,
    CardAlertResponders,
    CardAssignedDevices,
    CardIncidentHistory,
    PossiblePMS,
    CardClimateHistory,
    CardFacilityZone,
  },
  setup() {
    const { smartUnit, smartUnitLoading, fetchSmartUnit } = useSmartUnit();
    const { isAutoEnrollment } = usePMS();
    const { facilityFeatureEnabled } = useFacility();

    const editingUnit = ref("");
    const editingResponders = ref("");
    const smartZonesEnabled = computed(() =>
      facilityFeatureEnabled("smart_zones")
    );

    return {
      smartUnit,
      smartUnitLoading,
      fetchSmartUnit,
      editingResponders,
      editingUnit,
      isAutoEnrollment,
      smartZonesEnabled,
      formatDate,
    };
  },
  computed: {
    unitId() {
      return this.$route.params.zone_id;
    },
    facilityId() {
      return this.$route.params.organization_id;
    },
    renterId() {
      return this.$route.params.renter_id;
    },
    enrollmentMoveInAt() {
      return this.smartUnit?.enrollmentInfo?.moveInAt || null;
    },
    enrollmentStatus() {
      return this.smartUnit?.enrollmentInfo?.status || null;
    },
    isAwaitingDevice() {
      return this.enrollmentStatus === ENROLLMENT_STATUS.AWAITING_DEVICE;
    },
    isActiveEnrollment() {
      return this.enrollmentStatus === ENROLLMENT_STATUS.ACTIVE;
    },
    enrollmentDays() {
      return this.smartUnit?.enrollmentInfo?.daysWithoutDevice ?? null;
    },
    enrollmentDaysChipColor() {
      return awaitingDaysChipColor(this.enrollmentDays);
    },
    enrollmentDaysTextColor() {
      return awaitingDaysTextColor(this.enrollmentDays);
    },
  },
  watch: {
    editingResponders(editingResponders) {
      if (editingResponders) {
        this.editingUnit = "";
      }
    },
    editingUnit(editingUnit) {
      if (editingUnit) {
        this.editingResponders = "";
      }
    },
  },
  async beforeMount() {
    await this.fetchSmartUnit(this.$apollo, this.unitId);
  },
  methods: {
    daysWithoutDeviceLabel,
    gotoManageDevices() {
      this.$router.push({
        name: "ManageSmartUnitDevices",
        params: { zone_id: this.unitId },
      });
    },
    async refetchUnit() {
      await this.fetchSmartUnit(this.$apollo, this.unitId);
    },
  },
};
</script>

<style scoped>
/* Force a shared label-column width across the stacked Smart Unit
   Information / Alert Recipients / Facility Zone cards so their value
   columns line up, instead of each dl.property-list sizing its label
   column to its own longest label. */
.unit-summary-column ::v-deep .property-list {
  grid-template-columns: 140px auto;
}
</style>
